import { Box, Button, IconButton, Pagination, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import CompetenceItem from '@src/App/Components/Items/EntityItems/CompetenceItem/CompetenceItem';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import { Columns2, Plus, Rows2, ShieldUser, Sparkles, Target, TrendingUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import QuickLinksSection, { type QuickLinkTile } from './components/QuickLinksSection';
import StatsCards, { type StatsTile } from './components/StatsCards';
import SuggestedCompetencesWidget from './components/SuggestedCompetencesWidget';
import T4loAssistantInfoBox from './components/T4loAssistantInfoBox';

interface CompetenciesTabProps {
    employee: Employee;
    onTabChange?: (tabIndex: number) => void;
}

function CompetenciesTab({ employee, onTabChange }: CompetenciesTabProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const employeeCompetences = employee.competences || [];

    const [viewMode, setViewMode] = useState<'2-column' | '1-column'>('2-column');
    const currentLayout = isMobile ? '1-column' : viewMode;

    // Calculate stats
    const expertCompetences = employeeCompetences.filter((comp) => comp.level >= 3);
    const avgCompetenceLevel =
        employeeCompetences.length > 0
            ? employeeCompetences.reduce((sum, comp) => sum + comp.level, 0) / employeeCompetences.length
            : 0;

    const quickLinkTiles: QuickLinkTile[] = [
        {
            title: 'Kompetenzen bearbeiten',
            caption: 'Kompetenzen verwalten',
            icon: <ShieldUser size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
        },
        {
            title: 'Entwicklungsziele',
            caption: 'Ziele verwalten',
            icon: <TrendingUp size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
        },
        {
            title: 'Neues Ziel',
            caption: 'Entwicklungsziel hinzufügen',
            icon: <Target size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
        },
    ];

    const tiles: StatsTile[] = [
        {
            title: 'Kompetenzen',
            value: employeeCompetences.length.toString(),
            icon: <ShieldUser size={32} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        {
            title: 'Experten-Level',
            value: expertCompetences.length.toString(),
            icon: <Sparkles size={32} strokeWidth={1.5} />,
            color: '#ffb74d',
            bgColor: alpha('#ffb74d', 0.1),
            borderColor: alpha('#ffb74d', 0.3),
        },
        {
            title: 'Ø Level',
            value: avgCompetenceLevel.toFixed(1),
            icon: <TrendingUp size={32} strokeWidth={1.5} />,
            color: theme.palette.secondary.main,
            bgColor: alpha(theme.palette.secondary.main, 0.1),
            borderColor: alpha(theme.palette.secondary.main, 0.3),
        },
    ];

    const MissionCard = () => (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                    }}
                >
                    <Target size={20} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Nächste Lernpfad Mission
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Leadership Development Lernpfad
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Agile Project Management
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Entwickle deine Führungskompetenzen und werde zum Agile Coach. Diese Kompetenz ist der nächste Schritt
                in deinem Leadership-Lernpfad.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    size="small"
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 500,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                    }}
                >
                    Lernpfad ansehen
                </Button>

                <Button
                    size="small"
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 500,
                    }}
                >
                    Mission starten
                </Button>
            </Box>
        </Box>
    );

    const CompetenceManagement = () => {
        const [page, setPage] = useState(1);
        const itemsPerPage = 10;

        const paginatedCompetences = useMemo(() => {
            const startIndex = (page - 1) * itemsPerPage;
            return employeeCompetences.slice(startIndex, startIndex + itemsPerPage);
        }, [employeeCompetences, page]);

        const totalPages = Math.ceil(employeeCompetences.length / itemsPerPage);

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Meine Kompetenzen
                    </Typography>
                </Box>

                {employeeCompetences.length > 0 ? (
                    <>
                        <Stack spacing={1}>
                            {paginatedCompetences.map((competence) => {
                                const competenceData = allCompetences.find((c) => c.id === competence.id);
                                if (!competenceData) return null;

                                return (
                                    <CompetenceItem
                                        key={competence.id}
                                        id={competence.id}
                                        title={competenceData.title}
                                        level={competence.level}
                                        disableOverlay={true}
                                        onClick={() => navigate(`/${ELinks.COMPETENCE}/${competence.id}`)}
                                    />
                                );
                            })}
                        </Stack>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, newPage) => setPage(newPage)}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <ShieldUser size={48} style={{ color: theme.palette.text.secondary, marginBottom: '16px' }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Keine Kompetenzen vorhanden
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Füge deine ersten Kompetenzen hinzu, um sie hier zu sehen
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Plus />}
                            onClick={() => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`)}
                            sx={{ textTransform: 'none' }}
                        >
                            Kompetenzen hinzufügen
                        </Button>
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Kompetenz Manager
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Verwalte deine Kompetenzen und entwickle dich weiter
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`)}
                        aria-label="Kompetenzen bearbeiten"
                        title="Kompetenzen bearbeiten"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {getAppIcon(EIconCategory.EDIT)}
                    </IconButton>

                    {/* View Toggle */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                borderRadius: 1,
                                ml: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('2-column')}
                                sx={{
                                    color:
                                        viewMode === '2-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '2-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '4px 0 0 4px',
                                }}
                            >
                                <Columns2 size={16} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('1-column')}
                                sx={{
                                    color:
                                        viewMode === '1-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '1-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '0 4px 4px 0',
                                }}
                            >
                                <Rows2 size={16} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Content Layout */}
            {currentLayout === '2-column' ? (
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <StatsCards tiles={tiles} />
                        <CompetenceManagement />
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <T4loAssistantInfoBox context="general" />

                        <SuggestedCompetencesWidget />

                        <MissionCard />

                        <QuickLinksSection tiles={quickLinkTiles} layout="1-column" />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <T4loAssistantInfoBox context="general" />
                    <StatsCards tiles={tiles} />

                    {/* Competences Overview */}
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.secondary }}
                        >
                            Kompetenzen Übersicht
                        </Typography>
                        <CompetenceManagement />
                    </Box>

                    <QuickLinksSection tiles={quickLinkTiles} layout="1-column" />
                </Box>
            )}
        </Box>
    );
}

export default CompetenciesTab;
